<template>
  <div v-if="mountedLoad" class="is-relative">
    <div class="is-height-100p-60px px-20">
      <div class="columns is-height-100p">
        <div class="column is-9 mt-40 is-overflow-y-active">
          <div v-if="sansPanneaux" class="message is-warning mb-20">
            <div class="message-header">{{$t('recapRecom.sansPanneaux.title')}}</div>
            <div class="message-content pa-20 is-prewrap">
              <p>{{$t('recapRecom.sansPanneaux.message')}}</p>
            </div>
          </div>
          <RecapRecom
            v-if="recommandation"
            :societe="societe"
            :recom-devis="recommandation"
            :panneaux="panneauxReconstruits"
            :point-de-vente="pointDeVente"/>
        </div>
        <div class="column is-3 mt-40">
          <button
            v-if="recommandation.etat === constantes.ETAT_BROUILLON"
            @click="firstValidation"
            class="button is-fullwidth is-primary has-text-weight-bold mb-10">
            <span>{{$t('recapRecom.libelleBtnValiderRecom')}}</span>
          </button>
          <button
            v-if="recommandation.etat === constantes.ETAT_BROUILLON"
            class="button is-fullwidth mb-20"
            @click="modifierRecom(recommandation)">
            <span>{{$t('recapRecom.btnModifier')}}</span>
          </button>
          <DetailEnseigne
            :societe="societe"
            :recom="recommandation"
            :point-de-vente="pointDeVente"
            :is-send="isSend"
            @change-send="isSend = $event"
          />
        </div>
      </div>
    </div>
    <div class="is-modal-position column columns is-width-100p is-marginless is-overflow-y-active" v-if="isSend">
      <div class="column is-8 has-background-white is-paddingless">
        <modal-envoie-mail
          :idRecom="recommandation.id"
          :is-send="isSend"
          :recomPdf="fichierRecom"
          :societeId="societe.id"
          :mettreEdition="false"
          @change-send="isSend = $event">
        </modal-envoie-mail>
      </div>
      <div class="column has-background-transparent pointer"
        @click="isSend = !isSend"></div>
    </div>
    <b-modal
      :active.sync="isComponentValiderRecom"
      has-modal-card class="modal">
      <valider-definitivement
        :msg-errors="validationErrors"
        @clear-errors="validationErrors = []"
        @close="closeValidationModal"
        @valider="validation()"/>
    </b-modal>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import DetailEnseigne from '@/components/recommandation/detailRecommandation/DetailEnseigne.vue';
import RecapRecom from '@/components/recommandation/detailRecommandation/RecapRecom.vue';
import ModalEnvoieMail from '@/components/recommandation/detailRecommandation/modalEnvoieMail.vue';
import ValiderDefinitivement from '@/components/recommandation/detailRecommandation/validerDefinitivement.vue';
import gestionErreur from '@/services/general/gestionErreur';
import validationMessages from '@/services/recommandation/validation';
import devisToRecommandation, { lignesCommandeToPanneaux } from '@/services/recommandation/devisToRecommandation';
import { isValidPanneau } from '@/services/gestionPanneau/validationPanneau';
import * as typesUser from '@/store/modules/user-mutation-type';
import { PAGE_DEFAULT } from '@/router/routerConf';
import { ETAT_BROUILLON, ETAT_ENREGISTRE } from '@/configuration/devisConf';
import { brouillon2Enregistre } from '@/api/devis/index';

const Recommandations = createNamespacedHelpers('recommandations');
const User = createNamespacedHelpers('user');

export default {
  components: {
    DetailEnseigne,
    RecapRecom,
    ModalEnvoieMail,
    ValiderDefinitivement,
  },
  data: () => ({
    mountedLoad: false,
    societe: null,
    recommandation: null,
    isSend: false,
    fichierRecom: [],
    isComponentValiderRecom: false,
    validationErrors: [],
    constantes: { ETAT_BROUILLON },
		panneauxSousContrat: [],
  }),
  computed: {
    panneauxReconstruits() {
      if (!this.recommandation || !this.recommandation.lignesCommande) return [];
      const panneaux = lignesCommandeToPanneaux(this.recommandation.lignesCommande, this.societe, this.recommandation.issueRenouvellement);
      if (!this.recommandation.afficherPanneauxSousContrat) return panneaux;
      if (this.panneauxSousContrat.length) {
        return panneaux.concat(this.panneauxSousContrat.filter(p => p.actif && p.ligneRecommandationDevisId));
      }
      return panneaux;
    },
    validationBlocAnnuel() {
      if (!this.panneauxReconstruits.length) return false;
      return this.panneauxReconstruits.filter(p => p.ancienPanneau === "NON")
        .every(p => isValidPanneau(p));
    },
    pointDeVente() {
      if (!this.recommandation || !this.recommandation.intervenants) return null;
      const annonceur = this.recommandation.intervenants.find(i => i.annonceur);
      return annonceur ? annonceur.tiers : null;
    },
    sansPanneaux() {
      return this.panneauxReconstruits.filter(p => p.categorie).length === 0;
    }
  },
  methods: {
    ...User.mapMutations({
      setIsClick: typesUser.SET_IS_CLICK,
    }),
    ...Recommandations.mapActions({
      actionSaveRecomToStore: 'initialierRecommandationDevis',
      actionLoadProduitsLocation: 'loadProduitsLocation',
    }),
    async validation() {
      this.validationErrors = [];
      const loadingComponent = this.$buefy.loading.open();
      try {
        await brouillon2Enregistre(this.recommandation.id);
        this.recommandation.etat = ETAT_ENREGISTRE;
        this.isComponentValiderRecom = false;
        this.$router.push(PAGE_DEFAULT);
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error) {
          const msg = [];
          validationMessages(error.response, msg);
          if (msg.length) {
            this.validationErrors = msg.map(el => this.$t(el.message, el.parametres));
          }
        }
        gestionErreur(`Erreur lors de validation de la recom`, error);
      } finally {
        loadingComponent.close();
      }
    },
    closeValidationModal() {
      this.isComponentValiderRecom = false;
      this.validationErrors = [];
    },
    async modifierRecom(recom) {
      const loadingComponent = this.$buefy.loading.open();
      try {
        await this.actionSaveRecomToStore({ devisId: recom.id });
        this.$router.push({name: 'panier'});
      } catch (error) {
        gestionErreur(`Erreur lors de l'ajout de la recom ${recom.id} au panier`, error);
      } finally {
        loadingComponent.close();
      }
    },
    firstValidation() {
      if (!this.validationBlocAnnuel) {
        this.$buefy.toast.open({
          message: this.$t('recapRecom.messageErreur'),
          type: 'is-danger',
        });
        return;
      }
      this.validationErrors = [];
      this.isComponentValiderRecom = true;
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open({ container: null });
    try {
      await this.actionLoadProduitsLocation();
      const res = await devisToRecommandation(this.$route.params.id);
      this.societe = res.societe;
      this.recommandation = res.devis;
      this.panneauxSousContrat = res.panneauxSousContrat;
    } catch (error) {
      gestionErreur(this.$t('errors.errorRecuperationRecom', ({recom: this.$route.params.id})), error);
    } finally {
      loadingComponent.close();
      this.mountedLoad = true;
    }
  }
};
</script>
